import locationPinIcon from "assets/images/location-pin-small.svg";
import {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {appointmentCampaign as strings, common as stringsCommon} from "strings";

import Button from "components/Button";
import ConfirmAppointmentModal from "components/ConfirmAppointmentModal";
import Divider from "components/Divider";
import MobileNavigationHeader from "components/MobileNavigationHeader";
import PageTitle from "components/PageTitle";
import RadioButton from "components/RadioButton";

import * as models from "liberty-core/api/models";
import {useGetAppointmentQuestions} from "liberty-core/api/react-query/appointment";

import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./SelectAppointmentCampaignPage.module.css";

type LocationState = {
  guestInfo?: models.AppointmentGuestInfo;
  selectedPatientId: string | null;
  apptCampaigns: models.AppointmentCampaign[];
  appointmentType: string;
  selectedCampaignId?: string;
  insuranceInfo?: {frontImage: File; backImage: File};
  startDateTime?: string;
  multipleAppointmentTypes?: boolean;
};

function getTranslatedAppointmentType(
  appointmentType: models.AppointmentCampaignType,
) {
  switch (appointmentType) {
    case "Vaccination":
    case "Vacunación":
      return strings.vaccination;
    case "Test":
    case "Examen":
      return strings.test;
    case "Consultation":
    case "Consulta":
      return strings.consultation;
    case "Obsolete":
      return strings.obsolete;
    default: {
      return "";
    }
  }
}

export default function SelectAppointmentCampaignPage() {
  const [pharmacy] = useCurrentPharmacy();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const guestInfo = locationState.guestInfo;
  const selectedPatientId = locationState.selectedPatientId;
  const appointmentType = locationState.appointmentType;
  const apptCampaignsFull = locationState.apptCampaigns;
  const selectedCampaignIdPrev = locationState.selectedCampaignId;
  const insuranceInfo = locationState.insuranceInfo;
  const startDateTime = locationState.startDateTime;
  const multipleAppointmentTypes = locationState.multipleAppointmentTypes;

  const title =
    strings.title +
    " " +
    getTranslatedAppointmentType(
      appointmentType as models.AppointmentCampaignType,
    );
  const [apptCampaigns, setApptCampaigns] = useState<
    models.AppointmentCampaign[]
  >([]);

  useEffect(() => {
    setApptCampaigns(
      apptCampaignsFull.filter(
        (appt) => appt.appointmentSettingType === appointmentType,
      ),
    );
  }, [apptCampaignsFull, appointmentType]);

  const sortedApptCampaigns = apptCampaigns.sort((a, b) => {
    // sort appt campaigns alphabetically by name
    if (a.name > b.name) {
      return 1;
    } else if (a.name < b.name) {
      return -1;
    } else {
      return 0;
    }
  });

  const [selectedCampaignId, setSelectedCampaignId] = useState(
    selectedCampaignIdPrev ?? "",
  );
  const selectedCampaign = selectedCampaignId
    ? apptCampaigns.find((c) => c.id === selectedCampaignId)
    : undefined;
  const selectedCampaignOptionId =
    selectedCampaign && selectedCampaign.appointmentOptions?.length
      ? selectedCampaign.appointmentOptions[0].id
      : "";

  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState(false);

  const {data: appointmentQuestions, isLoading: isAppointmentQuestionsLoading} =
    useGetAppointmentQuestions(
      {
        pharmacyId: pharmacy?.id ?? 0,
        appointmentOptionId:
          selectedCampaign && selectedCampaign.appointmentOptions?.length
            ? selectedCampaign.appointmentOptions[0].id
            : "",
        startTime: startDateTime ?? "",
      },
      {
        enabled: !!startDateTime && selectedCampaignId !== "",
      },
    );

  const onNext = () => {
    const hasMoreThanOneOption =
      selectedCampaign?.appointmentOptions?.length &&
      selectedCampaign?.appointmentOptions?.length > 1;

    if (hasMoreThanOneOption) {
      navigate("/appointments/select-option", {
        state: {
          campaignName: selectedCampaign.name,
          options: selectedCampaign.appointmentOptions,
          selectedPatientId,
          guestInfo,
          apptCampaigns: apptCampaignsFull,
          appointmentType,
          selectedCampaignId,
          insuranceInfo,
          startDateTime,
        },
      });
      return;
    }
    if (!startDateTime) {
      navigate("/appointments/schedule", {
        state: {
          selectedCampaignOptionId: selectedCampaignOptionId,
          campaignName: selectedCampaign?.name ?? "",
          selectedPatientId,
          guestInfo,
          options: selectedCampaign && selectedCampaign.appointmentOptions,
          apptCampaigns,
          appointmentType,
          selectedCampaignId,
        },
      });
      return;
    }
    if (appointmentQuestions?.resultQuestions.length) {
      navigate("/appointments/questionnaire", {
        state: {
          campaignName: selectedCampaign?.name ?? "",
          options: selectedCampaign && selectedCampaign.appointmentOptions,
          selectedPatientId,
          guestInfo,
          apptCampaigns,
          appointmentType,
          selectedCampaignId,
          selectedCampaignOptionId: selectedCampaignOptionId,
          correlationId: appointmentQuestions.correlationId ?? "",
          resultQuestions: appointmentQuestions.resultQuestions ?? [],
          startDateTime,
          insuranceInfo,
          additionalVaccine: true,
        },
      });
    } else {
      setIsConfirmationModalVisible(true);
    }
  };

  useEffect(() => {
    if (!guestInfo && !selectedPatientId) {
      navigate("/appointments", {replace: true});
    }
  }, [navigate, guestInfo, selectedPatientId]);

  const showMultiApptMessage =
    pharmacy?.vaccinationsPerVisit &&
    pharmacy.vaccinationsPerVisit > 1 &&
    (appointmentType === "Vaccination" || appointmentType === "Vacunación") &&
    selectedPatientId &&
    !multipleAppointmentTypes;

  return (
    <div className={styles.container}>
      <MobileNavigationHeader
        onBack={() => {
          if (startDateTime) {
            navigate("/appointments");
            return;
          }
          const uniqueAppointmentTypes =
            apptCampaignsFull
              ?.map((c) => c.appointmentSettingType)
              .filter(
                (type, i) =>
                  apptCampaignsFull.findIndex(
                    (c) => c.appointmentSettingType === type,
                  ) === i,
              ) ?? [];
          if (uniqueAppointmentTypes?.length !== 1) {
            navigate("/appointments/new", {
              state: {
                apptCampaigns: apptCampaignsFull ?? [],
                guestInfo,
                selectedPatientId,
                appointmentType,
                insuranceInfo,
              },
            });
            return;
          }
          if (guestInfo) {
            navigate("/appointments/guest", {
              state: {
                apptCampaigns: apptCampaignsFull ?? [],
                guestInfo,
                insuranceInfo,
              },
            });
          } else {
            navigate("/appointments");
          }
        }}
        backLabel={startDateTime ? stringsCommon.cancel : undefined}
        alwaysShown
      />
      <PageTitle className={styles.title}>{title}</PageTitle>
      <div className={styles.location}>
        <img src={locationPinIcon} alt="" />
        <span className={styles.subtitle}>{pharmacy?.name}</span>
      </div>
      {showMultiApptMessage && (
        <div className={styles.multiVaccinesMessage}>
          {strings.multiVaccinesMessage(pharmacy.vaccinationsPerVisit)}
        </div>
      )}
      <Divider className={styles.divider} />
      {sortedApptCampaigns.map((campaign) => {
        return (
          <div key={campaign.id} className={styles.radioView}>
            <button
              key={campaign.id}
              onClick={() => {
                setSelectedCampaignId(campaign.id);
              }}
              className={styles.radioField}
            >
              <RadioButton
                key={campaign.id}
                checked={selectedCampaignId === campaign.id}
                className={styles.radioButton}
              />
              <div>
                <div className={styles.radioText}>{campaign.name}</div>
                {campaign.additionalInfo ? (
                  <div className={styles.radioText}>
                    {campaign.additionalInfo}
                  </div>
                ) : null}
              </div>
              <div
                className={
                  selectedCampaignId === campaign.id
                    ? styles.radioFieldSelected
                    : undefined
                }
              />
            </button>
            <Divider />
          </div>
        );
      })}
      <Button
        className={styles.proceed}
        disabled={selectedCampaignId === "" || isAppointmentQuestionsLoading}
        onClick={onNext}
      >
        {strings.next}
      </Button>
      <ConfirmAppointmentModal
        visible={isConfirmationModalVisible}
        onClose={() => setIsConfirmationModalVisible(false)}
        guestInfo={guestInfo}
        selectedPatientId={selectedPatientId}
        campaignName={selectedCampaign?.name ?? ""}
        selectedCampaignOptionId={selectedCampaignOptionId}
        correlationId={appointmentQuestions?.correlationId ?? ""}
        startDateTime={startDateTime}
        responses={null}
        insuranceInfo={insuranceInfo}
        appointmentType={appointmentType}
      />
    </div>
  );
}
